import {
  ActionIcon,
  Box,
  Flex,
  Image,
  Paper,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import fundTranfer from "assets/images/pngs/searchicon1.png";
import termdeposit from "assets/images/pngs/searchicon2.png";
import loan from "assets/images/pngs/searchicon3.png";
import card from "assets/images/pngs/searchicon4.png";
import accountsummary from "assets/images/pngs/searchicon5.png";
import Settings from "assets/images/pngs/searchicon6.png";

const searchItems = [
  { image: fundTranfer, label: "FUND_TRANSFER" },
  { image: termdeposit, label: "TERM_DEPOSIT" },
  { image: loan, label: "LOAN" },
  { image: card, label: "CARD" },
  { image: accountsummary, label: "ACCOUNT_SUMMARY" },
  { image: Settings, label: "SETTING" },
];

type SearchBarProps = {
  // #55667: bung sẵn ô nhập thay vì chỉ hiện icon kính lúp.
  // MẶC ĐỊNH PHẢI LÀ `false` — thu gọn là hành vi CÓ CHỦ Ý của header toàn app
  // (bấm ra ngoài / Escape là đóng lại). Chỉ module Bảo lãnh bật cờ này vì bản
  // thiết kế của riêng module đó vẽ ô tìm kiếm mở sẵn. Đổi mặc định thành `true`
  // là đổi header của MỌI nghiệp vụ khác — đừng làm.
  alwaysExpanded?: boolean;
};

const SearchBar = ({ alwaysExpanded = false }: SearchBarProps) => {
  const { t } = useTranslation();
  const [isSearching, setIsSearching] = useState(false);
  // #55667: khung gợi ý có state RIÊNG, tách khỏi trạng thái bung của ô nhập.
  // Trước đây hai thứ này dùng chung một điều kiện nên khi bật `alwaysExpanded`
  // thì khung gợi ý cũng mở vĩnh viễn và che kín nội dung màn /guarantee/*.
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Lưu nội dung nhập vào
  const searchRef = useRef<HTMLDivElement>(null);

  // #55667: trạng thái bung được SUY RA, không khởi tạo một lần bằng
  // `useState(alwaysExpanded)`. Lý do: `HeaderBar` nằm trong `LoggedInLayout` và
  // `PrivateRoute` render layout bọc `useOutlet()`, nên component này KHÔNG remount
  // khi đổi route. Khởi tạo theo state sẽ kẹt giá trị của màn đầu tiên: vào
  // /guarantee từ màn khác thì ô vẫn đóng, rời /guarantee thì ô vẫn mở.
  // - `isExpanded`  : ô nhập có bung hay không.
  // - `isSuggestOpen`: khung gợi ý có mở hay không (chỉ khi người dùng đang tương tác).
  const isExpanded = alwaysExpanded || isSearching;

  // Lọc danh sách dựa trên searchTerm
  const filteredItems = searchItems.filter((item) =>
    t(item.label).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Xử lý sự kiện click ngoài ô tìm kiếm / nhấn Escape
  useEffect(() => {
    // #55667: listener LUÔN được đăng ký, kể cả chế độ bung sẵn — vì ở chế độ đó
    // vẫn cần đóng khung gợi ý khi bấm ra ngoài / nhấn Escape. Vòng trước chặn cả
    // useEffect bằng `if (alwaysExpanded) return undefined;` nên khung gợi ý không
    // bao giờ đóng được.
    //
    // Hai setter dưới đây chạy chung cho cả hai chế độ:
    //   - `setIsSuggestOpen(false)` : đóng khung gợi ý — áp dụng mọi chế độ.
    //   - `setIsSearching(false)`   : thu gọn ô nhập — chỉ có tác dụng THẤY ĐƯỢC ở chế độ
    //     thường; khi `alwaysExpanded` thì `isExpanded` vẫn true nên ô nhập giữ nguyên bung.
    // Nhờ vậy không cần rẽ nhánh, và cleanup luôn gỡ đúng listener đã đăng ký.
    const closeAll = () => {
      setIsSuggestOpen(false);
      setIsSearching(false);
    };

    // #55667: dùng `mousedown` ngoài vùng `searchRef` thay cho `onBlur` của TextInput.
    // `onBlur` bắn TRƯỚC `click`, nên nếu đóng khung gợi ý ở `onBlur` thì người dùng
    // bấm vào một mục gợi ý sẽ bị gỡ mục đó khỏi DOM trước khi click kịp ăn.
    // `searchRef` bọc cả ô nhập lẫn khung gợi ý nên bấm trong khung không bị coi là bấm ngoài.
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        closeAll();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAll();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      {isExpanded ? (
        <Box ref={searchRef} sx={{ position: "relative", width: 400 }}>
          <TextInput
            // #55667: chỉ autoFocus khi người dùng CHỦ ĐỘNG bấm kính lúp để mở.
            // Ở chế độ bung sẵn, ô có mặt ngay lúc tải trang nên autoFocus sẽ cướp
            // con trỏ khỏi nội dung màn.
            autoFocus={!alwaysExpanded}
            value={searchTerm}
            // #55667: khung gợi ý chỉ mở khi người dùng THẬT SỰ tương tác — bấm vào ô
            // (focus) hoặc gõ chữ. Ở chế độ bung sẵn, vào màn là ô rỗng và chưa có gợi ý.
            onFocus={() => setIsSuggestOpen(true)}
            // #55667: cần thêm `onClick` bên cạnh `onFocus`. Ở chế độ bung sẵn, sau khi
            // bấm ra ngoài để đóng khung gợi ý, con trỏ có thể VẪN nằm trong ô nhập —
            // lúc đó bấm lại vào ô sẽ không bắn `onFocus` nữa nên khung gợi ý không mở lại.
            onClick={() => setIsSuggestOpen(true)}
            onChange={(event) => {
              setSearchTerm(event.currentTarget.value);
              setIsSuggestOpen(true);
            }}
            // #55667: chủ dự án soát trên DEV báo thiếu placeholder. Lấy đúng câu của
            // bản thiết kế (`Bao_Lanh_Du_Thau.HTM:446`), đi qua i18n — KHÔNG hardcode
            // chuỗi trong .tsx vì app chạy hai ngôn ngữ vi/en.
            placeholder={t("PLACEHOLDER_GLOBAL_SEARCH")}
            sx={{
              input: {
                borderColor: "#D8DDE2",
                // #55667: bo góc lấy theo `.topsearch input` của bản thiết kế
                // (`Bao_Lanh_Du_Thau.HTM:67` — `border-radius:22px`). Trước đây để 8px
                // nên ô hiện ra vuông góc, lệch thiết kế.
                borderRadius: "22px",
                // #55667: height 38px + font 13.5px cũng lấy từ dòng thiết kế nói trên.
                // ĐÃ KIỂM là không làm lệch chiều cao hàng header: thanh toolbar trong
                // `HeaderBar.tsx` bị ghim cứng `height: "64px"` với `alignItems: "center"`,
                // và phần tử cao nhất cùng hàng là `Avatar size={40}` — 38px vẫn thấp hơn
                // cả hai nên hàng không bị đẩy. (Mặc định Mantine size "sm" là 36px.)
                height: "38px",
                // `lineHeight` phải đi kèm khi đè `height`: Mantine size "sm" đặt sẵn
                // line-height 34px cho khung 36px; giữ nguyên 34px trong khung 38px sẽ
                // làm chữ lệch lên 1px so với tâm ô.
                lineHeight: "36px",
                fontSize: "13.5px",
                "&:focus": { borderColor: "#2A81D0" },
              },
            }}
            icon={<IconSearch size={24} color="#989FA6" />}
          />
          {isSuggestOpen && (
            <Paper
              shadow="sm"
              p={16}
              pb={8}
              mt={2}
              sx={{
                position: "absolute",
                width: "100%",
                zIndex: 99,
                backgroundColor: "#FDFDFD",
                borderRadius: "8px",
              }}
            >
              <Flex direction="column" gap={8}>
                <Text fw={700} fz={16} sx={{ color: "#2E3337" }}>
                  {searchTerm.length === 0 ? t("SUGGEST") : t("SEARCH_RESULTS")}
                </Text>
                <Flex direction="column">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item, index) => (
                      <Flex
                        key={item.label}
                        px={8}
                        py={16}
                        align="center"
                        gap={16}
                        sx={{
                          borderBottom:
                            index !== filteredItems.length - 1
                              ? "1px solid #F0F2F7"
                              : "transparent",
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#F0F2F7" },
                        }}
                      >
                        <Box style={{ width: "22px", height: "auto" }}>
                          <Image src={item.image} />
                        </Box>
                        <Text style={{ fontSize: "14px" }}>{t(item.label)}</Text>
                      </Flex>
                    ))
                  ) : (
                    <Text
                      fz={14}
                      fw={400}
                      sx={{
                        color: "#2E3337",
                        textAlign: "center",
                        marginBottom: "16px",
                      }}
                    >
                      {t("SEARCH_RESULTS")}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Paper>
          )}
        </Box>
      ) : (
        <ActionIcon
          variant="light"
          // #55667: chế độ thường giữ nguyên hành vi cũ — bấm kính lúp mở CẢ ô nhập
          // lẫn khung gợi ý trong cùng một thao tác.
          onClick={() => {
            setIsSearching(true);
            setIsSuggestOpen(true);
          }}
          sx={{
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "transparent" },
            color: "#2E3337",
          }}
        >
          <IconSearch size={20} />
        </ActionIcon>
      )}
    </Box>
  );
};

export default SearchBar;
